import type { Placement } from 'react-joyride';

export interface GuidedTourRouteContext
{
    hasOrganization: boolean;
    isAdmin: boolean;
    projectRoute: string | null;
    currentProjectRoute: string | null;
}

export type GuidedTourPrepareAction = 'openOrganizationCreateModal' | 'showBillingPaymentForm' | 'showAzureDevOpsProjectFields';

export interface GuidedTourStepConfig
{
    id: string;
    route: string | ((context: GuidedTourRouteContext) => string);
    target: string | ((context: GuidedTourRouteContext) => string);
    title: string;
    content: string;
    placement?: Placement;
    requiresMilestone?: 'organizationCreated' | 'billingDecisionCompleted' | 'projectCreated';
    prepareAction?: GuidedTourPrepareAction;
    projectWizardStep?: 1 | 2 | 3 | 4;
    optionalTarget?: boolean;
}

export const DASHBOARD_GUIDED_TOUR_STEPS: GuidedTourStepConfig[] = [
    {
        id: 'organization-open-create',
        route: '/admin/organizations?guidedTour=1',
        target: '#organization-management-create-action',
        title: 'Create Organization',
        content: 'Open the organization creation form here. This is the real organization page, so the setup starts where admins manage organizations later.',
        placement: 'bottom',
    },
    {
        id: 'organization-name',
        route: '/admin/organizations?guidedTour=1',
        target: '#organization-create-name',
        title: 'Organization Name',
        content: 'Enter the workspace name that will own users, quota, projects, and generated assets.',
        placement: 'bottom',
        prepareAction: 'openOrganizationCreateModal',
    },
    {
        id: 'organization-type',
        route: '/admin/organizations?guidedTour=1',
        target: '#organization-create-type',
        title: 'Organization Type',
        content: 'Choose the closest organization category so the account record has useful business context.',
        placement: 'bottom',
        prepareAction: 'openOrganizationCreateModal',
    },
    {
        id: 'organization-size',
        route: '/admin/organizations?guidedTour=1',
        target: '#organization-create-size',
        title: 'Organization Size',
        content: 'Capture the approximate team size. This stays optional, but it helps future account setup decisions.',
        placement: 'bottom',
        prepareAction: 'openOrganizationCreateModal',
    },
    {
        id: 'organization-submit',
        route: '/admin/organizations?guidedTour=1',
        target: '#organization-create-submit',
        title: 'Save Organization',
        content: 'Create the organization when the details look right. After it saves, onboarding continues on billing.',
        placement: 'top',
        prepareAction: 'openOrganizationCreateModal',
    },
    {
        id: 'billing-decision',
        route: '/billing?guidedTour=1',
        target: '#billing-onboarding-decision',
        title: 'Choose a Billing Path',
        content: 'Continue with available free quota or save a card and activate the subscription before running test generation.',
        placement: 'bottom',
        requiresMilestone: 'organizationCreated',
    },
    {
        id: 'billing-free-quota',
        route: '/billing?guidedTour=1',
        target: '#billing-continue-free-quota',
        title: 'Free Quota Path',
        content: 'Use this action to continue immediately with available free quota. A card is not required for this path.',
        placement: 'bottom',
        requiresMilestone: 'organizationCreated',
    },
    {
        id: 'billing-subscription-plan',
        route: '/billing?guidedTour=1',
        target: '#billing-subscription-plan-card',
        title: 'Subscription Plan',
        content: 'Review the paid plan details here if you prefer to activate a subscription before generating test cases.',
        placement: 'right',
        requiresMilestone: 'organizationCreated',
    },
    {
        id: 'billing-payment-method',
        route: '/billing?guidedTour=1',
        target: '#billing-payment-method-card',
        title: 'Payment Method',
        content: 'This card holds the Stripe payment method used for subscription renewals.',
        placement: 'left',
        requiresMilestone: 'organizationCreated',
    },
    {
        id: 'billing-card-details',
        route: '/billing?guidedTour=1',
        target: '#billing-card-details-field',
        title: 'Card Details',
        content: 'Enter card details here when choosing the subscription path. Stripe handles the sensitive payment fields.',
        placement: 'left',
        requiresMilestone: 'organizationCreated',
        optionalTarget: true,
    },
    {
        id: 'billing-save-payment-method',
        route: '/billing?guidedTour=1',
        target: '#billing-save-payment-method',
        title: 'Save Payment Method',
        content: 'Save the card first, then use the subscription action to activate the paid path.',
        placement: 'top',
        requiresMilestone: 'organizationCreated',
        optionalTarget: true,
    },
    {
        id: 'billing-subscribe',
        route: '/billing?guidedTour=1',
        target: '#billing-subscribe-action',
        title: 'Activate Subscription',
        content: 'After a payment method is saved, subscribe here. Either this path or free quota will continue onboarding.',
        placement: 'top',
        requiresMilestone: 'organizationCreated',
    },
    {
        id: 'projects-create-entry',
        route: '/projects?guidedTour=1',
        target: '#projects-create-action',
        title: 'Create Project',
        content: 'Start a new project workspace from here. The guided setup continues through project details, optional knowledge, and requirements upload.',
        placement: 'bottom',
    },
    {
        id: 'project-name',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-name',
        title: 'Project Name',
        content: 'Name the real project workspace that will hold requirement documents and generated test cases.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-business-context',
        route: '/projects/new?guidedTour=1',
        target: '#project-domain-description',
        title: 'Business Context',
        content: 'Describe the application, users, workflows, and constraints that should shape generated knowledge.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-type',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-type',
        title: 'Project Type',
        content: 'Choose between a document-first project and an Azure DevOps-backed project.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-simple-option',
        route: '/projects/new?guidedTour=1',
        target: '#project-type-simple',
        title: 'Simple Project Option',
        content: 'Use this option for a document-first project that does not need Azure DevOps work item or repository configuration.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-azure-option',
        route: '/projects/new?guidedTour=1',
        target: '#project-type-azure',
        title: 'Azure DevOps Option',
        content: 'Use this option when the project should connect to Azure work items and repository context.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-azure-organization',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-azure-org',
        title: 'Azure Organization',
        content: 'For Azure DevOps projects, enter the Azure organization that owns the boards and repositories.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-azure-project',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-azure-project',
        title: 'Azure Project',
        content: 'Point the app to the Azure DevOps project that contains the work items for this setup.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-azure-team',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-azure-team',
        title: 'Azure Team',
        content: 'Enter the Azure DevOps team that owns the backlog or board being used.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-azure-pat',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-azure-pat',
        title: 'Azure Access Token',
        content: 'Add the Azure DevOps personal access token only when connecting this project to Azure.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-step-one-next',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-step1-next',
        title: 'Continue Project Setup',
        content: 'Move from the basics into the optional domain-knowledge step after the required fields are ready.',
        placement: 'top',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 1,
    },
    {
        id: 'project-domain-notes',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-domain-notes',
        title: 'Domain Notes',
        content: 'Add extra domain context such as technical constraints, regulations, or user expectations.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 2,
    },
    {
        id: 'project-domain-documents',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-domain-documents',
        title: 'Domain Documents',
        content: 'Upload supporting domain material when you have it. This step can also be skipped in the project wizard.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 2,
    },
    {
        id: 'project-step-two-next',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-step2-next',
        title: 'Continue or Skip',
        content: 'Continue to enterprise knowledge, or use the wizard skip link if this project does not need domain uploads now.',
        placement: 'top',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 2,
    },
    {
        id: 'project-enterprise-overview',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-overview',
        title: 'Enterprise Overview',
        content: 'Summarize internal context that is specific to your organization and useful for generated tests.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-enterprise-systems',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-systems',
        title: 'Internal Systems',
        content: 'List internal systems that influence integration, data, or workflow expectations.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-enterprise-acronyms',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-acronyms',
        title: 'Acronyms and Glossary',
        content: 'Paste acronyms or internal terms so generated artifacts use language your team recognizes.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-enterprise-process',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-process',
        title: 'Process Notes',
        content: 'Document approval chains, handoffs, and internal workflows that should affect test coverage.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-enterprise-testing',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-testing',
        title: 'Testing Cautions',
        content: 'Add known caveats, environment notes, and areas the generated tests should treat carefully.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-enterprise-documents',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-enterprise-documents',
        title: 'Enterprise Documents',
        content: 'Attach private knowledge sources such as SOPs or architecture notes when they are available.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-step-three-next',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-step3-next',
        title: 'Review Project Setup',
        content: 'Continue to review, or skip this optional section if enterprise knowledge is not needed yet.',
        placement: 'top',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 3,
    },
    {
        id: 'project-review-basics',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-review-basics',
        title: 'Review Basics',
        content: 'Confirm the project name, type, and business context before creation.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 4,
    },
    {
        id: 'project-review-domain',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-review-domain',
        title: 'Review Domain Knowledge',
        content: 'Check the optional domain context that will seed generated project knowledge.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 4,
    },
    {
        id: 'project-review-enterprise',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-review-enterprise',
        title: 'Review Enterprise Knowledge',
        content: 'Confirm any internal knowledge before creating the project.',
        placement: 'bottom',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 4,
    },
    {
        id: 'project-submit',
        route: '/projects/new?guidedTour=1',
        target: '#project-create-review-submit',
        title: 'Create Project',
        content: 'Create the project shell. The tour continues on the project requirements tab after this action succeeds.',
        placement: 'top',
        requiresMilestone: 'billingDecisionCompleted',
        projectWizardStep: 4,
    },
    {
        id: 'upload-docs-section',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-upload-section',
        title: 'Upload Documents',
        content: 'This upload card accepts requirement files for the project you just created.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'upload-docs-dropzone',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-upload-dropzone',
        title: 'Select Requirement Files',
        content: 'Drop requirement files here or browse from disk. The selected files become generation inputs after upload.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'upload-docs-classification',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-upload-file-classification',
        title: 'Choose a Classification',
        content: 'Each uploaded requirement file needs a classification before it can be uploaded for test case generation.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'upload-docs-context',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-upload-file-context',
        title: 'Add Context Description',
        content: 'Describe what the file contains and why it matters. This context is required before the document can be uploaded.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'upload-docs-submit',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-upload-submit',
        title: 'Upload Requirement Files',
        content: 'Upload the selected and classified files so they can be processed and selected for generation.',
        placement: 'top',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'requirements-document-selection',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-document-selection',
        title: 'Select Ready Inputs',
        content: 'Ready documents are selected here before running Test Case Creation.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'requirements-document-selection-ready',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-document-selection-first-ready',
        title: 'Select a Ready Document',
        content: 'Select at least one ready document here so the generator knows which uploaded source to use.',
        placement: 'bottom',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'generate-tests-summary',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-generate-action',
        title: 'Generation Summary',
        content: 'This bar confirms whether the selected documents or work items are ready for test generation.',
        placement: 'top',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'generate-tests-button',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-generate-button',
        title: 'Generate Test Cases',
        content: 'Start Test Case Creation here. Finishing this tutorial will take you back to the dashboard.',
        placement: 'top',
        requiresMilestone: 'projectCreated',
    },
    {
        id: 'generate-tests-confirm',
        route: (context) => context.projectRoute || context.currentProjectRoute || '/projects',
        target: '#requirements-review-confirm-generate',
        title: 'Confirm Generation',
        content: 'After the review opens, confirm the generation run from this action.',
        placement: 'top',
        requiresMilestone: 'projectCreated',
        optionalTarget: true,
    },
];

export function getGuidedTourStepIndexById(stepId: string): number
{
    return Math.max(0, DASHBOARD_GUIDED_TOUR_STEPS.findIndex((step) => step.id === stepId));
}

export function resolveGuidedTourRoute(
    step: GuidedTourStepConfig,
    context: GuidedTourRouteContext,
): string
{
    return typeof step.route === 'function' ? step.route(context) : step.route;
}

export function resolveGuidedTourTarget(
    step: GuidedTourStepConfig,
    context: GuidedTourRouteContext,
): string
{
    return typeof step.target === 'function' ? step.target(context) : step.target;
}
